import {
  ISelectType,
  OverflowTooltip,
  Table,
  useFetch,
  useNotificationContext,
} from "@4uhub/lib4uhub";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { endOfDay, format, startOfDay } from "date-fns";
import { enUS, es, ptBR } from "date-fns/locale";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import useCrmLink from "../../../../hooks/useCrmLink";
import { ITicketHistoryLog } from "../../../../models/ticket-history-log";
import MainApi from "../../../../services/mainApi.service";
import TicketServService from "../../../../services/ticket.service";
import {
  TICKET_HISTORY_LOG_ROUTE,
  getTicketHistoryOccurrenceTypes,
  getTicketHistoryTicketById,
  getTicketHistoryTicketChannelById,
} from "../../../../services/ticketHistoryLog.service";
import MacroFilter, { IFilterParams } from "./MacroFilter";
import TicketHistoryMessageModal from "./TicketHistoryMessageModal";
import {
  canListTicketHistories,
  DEFAULT_DATE_RANGE_OPTION_ID,
} from "./ticketHistoriesFilters";

const ticketHistoryLogService = new MainApi<ITicketHistoryLog>(
  TICKET_HISTORY_LOG_ROUTE,
);

const ticketService = new TicketServService();

const translationPath = "chatBot.page.logs.ticketHistories.";

const TicketHistories = () => {
  const { t, i18n } = useTranslation();

  const { crmClickHandler } = useCrmLink();
  const { setMessage } = useNotificationContext();

  const [refresh, setRefresh] = useState(0);
  const [isLoadingFilters, setIsLoadingFilters] = useState(true);
  const [filtersError, setFiltersError] = useState(false);
  const [occurrenceTypeOptions, setOccurrenceTypeOptions] = useState<
    ISelectType[]
  >([]);
  const [ticketServiceOptions, setTicketServiceOptions] = useState<
    ISelectType[]
  >([]);
  const [selectedMessage, setSelectedMessage] = useState<string | null>(null);

  const today = useMemo(() => new Date(), []);

  const [filterParams, setFilterParams] = useState<IFilterParams>({
    DateRangeOptionId: DEFAULT_DATE_RANGE_OPTION_ID,
    StartDate: today,
    EndDate: today,
    OccurrenceTypeIds: [],
    TicketServiceIds: [],
    Search: "",
  });

  const { sendRequest: listOccurrenceTypes } = useFetch(
    getTicketHistoryOccurrenceTypes,
  );
  const { sendRequest: listTicketServices } = useFetch(
    ticketService.ticketServiceList,
  );
  const { sendRequest: fetchTicketById } = useFetch(getTicketHistoryTicketById);
  const { sendRequest: fetchTicketChannelById } = useFetch(
    getTicketHistoryTicketChannelById,
  );

  const formatDate = useCallback(
    (value?: string | null) => {
      if (!value) return "-";

      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "-";

      return format(date, "Pp", {
        locale:
          i18n.language === "pt-BR"
            ? ptBR
            : i18n.language === "en-US"
              ? enUS
              : es,
      }).replaceAll(",", "");
    },
    [i18n.language],
  );

  const loadFilterOptions = useCallback(async () => {
    setIsLoadingFilters(true);
    setFiltersError(false);

    const [occurrenceResponse, ticketServicesResponse] = await Promise.all([
      listOccurrenceTypes(undefined),
      listTicketServices(undefined),
    ]);

    if (
      !occurrenceResponse.success ||
      !ticketServicesResponse.success ||
      !occurrenceResponse.data ||
      !ticketServicesResponse.data
    ) {
      setFiltersError(true);
      setIsLoadingFilters(false);
      return;
    }

    setOccurrenceTypeOptions(occurrenceResponse.data);
    setTicketServiceOptions(ticketServicesResponse.data);
    setIsLoadingFilters(false);
  }, [listOccurrenceTypes, listTicketServices]);

  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  const handleFilterChange = useCallback(
    (newFilters: IFilterParams | ((prev: IFilterParams) => IFilterParams)) => {
      setFilterParams(newFilters);
      setRefresh((prev) => prev + 1);
    },
    [],
  );

  const handleTicketClick = useCallback(
    async (ticketId: string) => {
      const { data: ticket, success: ticketSuccess } =
        await fetchTicketById(ticketId);

      const ticketChannelId = ticket?.ticketChannelStep?.ticketChannelId;

      if (!ticketSuccess || !ticketChannelId) {
        setMessage({
          message: t(
            translationPath + "errors.ticket_redirect",
            "Unable to open the ticket in CRM.",
          ),
          type: "error",
        });
        return;
      }

      const { data: ticketChannel, success: ticketChannelSuccess } =
        await fetchTicketChannelById(ticketChannelId);

      const ticketWorkspaceId = ticketChannel?.ticketWorkspace?.id;

      if (!ticketChannelSuccess || !ticketWorkspaceId) {
        setMessage({
          message: t(
            translationPath + "errors.ticket_redirect",
            "Unable to open the ticket in CRM.",
          ),
          type: "error",
        });
        return;
      }

      crmClickHandler(
        `/w/${ticketWorkspaceId}/boards/${ticketChannelId}/card/${ticketId}`,
      );
    },
    [crmClickHandler, fetchTicketById, fetchTicketChannelById, setMessage, t],
  );

  const columns: GridColDef<ITicketHistoryLog>[] = [
    {
      field: "created",
      headerName: t(translationPath + "created") ?? "Criado",
      flex: 0.9,
      minWidth: 180,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: "occurrenceType",
      headerName:
        t(translationPath + "occurrence_type") ?? "Tipo de ocorrência",
      flex: 0.9,
      minWidth: 220,
      renderCell: (params) => params.value?.name ?? "-",
    },
    {
      field: "ticketServiceName",
      headerName: t(translationPath + "ticket_service") ?? "Serviço de ticket",
      flex: 1,
      minWidth: 220,
      renderCell: (params) =>
        params.value ? (
          <OverflowTooltip
            text={params.value}
            tooltip={params.value}
            textTransform="none"
            variant="body2"
          />
        ) : (
          "-"
        ),
    },
    {
      field: "message",
      headerName: t(translationPath + "message") ?? "Mensagem",
      flex: 1.6,
      minWidth: 320,
      sortable: false,
      renderCell: (params) => {
        const message = params.value ?? "";

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: "100%",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                flex: 1,
              }}
            >
              {message || "-"}
            </Typography>
            {message && (
              <Tooltip
                title={
                  t(translationPath + "view_full_message") ??
                  "Ver mensagem completa"
                }
              >
                <IconButton
                  size="small"
                  onClick={() => setSelectedMessage(message)}
                  sx={{ padding: 0.5 }}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: "ticketId",
      headerName: t(translationPath + "ticket_id") ?? "Ticket",
      flex: 1,
      minWidth: 220,
      renderCell: (params) => (
        <Tooltip
          title={t(translationPath + "open_ticket") ?? "Abrir ticket no CRM"}
        >
          <Link
            component="button"
            variant="body2"
            underline="hover"
            sx={{
              textAlign: "left",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            onClick={() => handleTicketClick(params.value)}
          >
            {params.value}
          </Link>
        </Tooltip>
      ),
    },
  ];

  const serviceProps = useMemo(
    () => ({
      refresh,
      StartDate: filterParams.StartDate
        ? startOfDay(filterParams.StartDate).toISOString()
        : undefined,
      EndDate: filterParams.EndDate
        ? endOfDay(filterParams.EndDate).toISOString()
        : undefined,
      OccurrenceTypeIds:
        filterParams.OccurrenceTypeIds.length > 0
          ? filterParams.OccurrenceTypeIds
          : undefined,
      TicketServiceIds:
        filterParams.TicketServiceIds.length > 0
          ? filterParams.TicketServiceIds
          : undefined,
      SearchBy: filterParams.Search.trim() || undefined,
    }),
    [filterParams, refresh],
  );

  const shouldListTicketHistories = useMemo(
    () => canListTicketHistories(filterParams),
    [filterParams],
  );

  const macroFilters = useCallback(
    () => (
      <MacroFilter
        filterParams={filterParams}
        onFilterParams={handleFilterChange}
        occurrenceTypeOptions={occurrenceTypeOptions}
        ticketServiceOptions={ticketServiceOptions}
      />
    ),
    [
      filterParams,
      handleFilterChange,
      occurrenceTypeOptions,
      ticketServiceOptions,
    ],
  );

  if (isLoadingFilters) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{ minHeight: 320, width: "100%" }}
      >
        <CircularProgress />
      </Stack>
    );
  }

  if (filtersError) {
    return (
      <Stack
        spacing={2}
        alignItems="flex-start"
        justifyContent="center"
        sx={{ minHeight: 320 }}
      >
        <Alert severity="error">
          {t(translationPath + "errors.filters_load") ??
            "Erro ao carregar os filtros"}
        </Alert>
        <Button variant="outlined" onClick={loadFilterOptions}>
          {t(translationPath + "retry")}
        </Button>
      </Stack>
    );
  }

  return (
    <>
      {selectedMessage && (
        <TicketHistoryMessageModal
          message={selectedMessage}
          onClose={() => setSelectedMessage(null)}
        />
      )}
      <Table<ITicketHistoryLog>
        title={t(translationPath + "title") ?? "Logs dos históricos de tickets"}
        columns={columns}
        showDefaultMacroFilters={false}
        showButton={false}
        showDeleteButton={false}
        defaultActions={false}
        searchInput={false}
        searchInputPlaceHolder=""
        getRowId={(row) => row.id}
        macroFilters={macroFilters}
        {...(shouldListTicketHistories
          ? {
              service: ticketHistoryLogService,
              serviceProps,
            }
          : {
              rows: [],
            })}
      />
    </>
  );
};

export default TicketHistories;
